/**
 * The map behind the Order-now sheet's delivery step: Leaflet with OpenStreetMap tiles, and
 * one Nominatim lookup to place the pin. Free tier, POC — production wants a self-hosted or
 * paid geocoder (see ai-work/delivery-tips/delivery-plan.md).
 */
import type * as Leaflet from 'leaflet';
import { DeliverySettings, LatLng } from './order-delivery';

export type LeafletModule = typeof Leaflet;

let leaflet: Promise<LeafletModule> | null = null;

/**
 * Leaflet, loaded the first time a delivery map is shown — never for a venue without
 * delivery pricing, and never for dine-in.
 *
 * The script is a lazy chunk (dynamic import). The stylesheet is built as its own
 * non-injected bundle (`leaflet` in angular.json) and linked here, because without it the
 * tiles render as a scattered column of images.
 */
export function loadLeaflet(): Promise<LeafletModule> {
  if (!leaflet) {
    if (!document.querySelector('link[data-leaflet]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'leaflet.css';
      link.setAttribute('data-leaflet', '');
      document.head.appendChild(link);
    }
    leaflet = import('leaflet').then(m => ((m as any).default ?? m) as LeafletModule);
    // A failed load (offline) must not poison every later attempt.
    leaflet.catch(() => { leaflet = null; });
  }
  return leaflet;
}

/**
 * Nominatim's usage policy: at most one request per second, and NO type-ahead. So this is
 * called once, when the guest asks to find their typed address, and the caller is held to
 * the rate below rather than trusting the button to be slow enough.
 */
const NOMINATIM_MIN_INTERVAL_MS = 1100;
let lastNominatimRequest = 0;

/**
 * Wait for this caller's turn. The slot is RESERVED before waiting, so a forward lookup and a
 * reverse one started together still go out 1.1 s apart rather than both reading the same
 * "last request" time.
 */
async function nominatimTurn(): Promise<void> {
  const slot = Math.max(Date.now(), lastNominatimRequest + NOMINATIM_MIN_INTERVAL_MS);
  lastNominatimRequest = slot;
  const wait = slot - Date.now();
  if (wait > 0) {
    await new Promise(resolve => setTimeout(resolve, wait));
  }
}

/**
 * The typed address as a point, or null when Nominatim has nothing (or cannot be reached) —
 * the caller then starts the pin at the venue for the guest to drag.
 *
 * Biased towards the venue with a viewbox around its widest area, but not bounded to it
 * (`bounded=0`): a street name shared by two towns should resolve to the one nearby, yet an
 * address outside every area must still be found, so the guest is told it is out of range
 * instead of being shown a pin somewhere unrelated.
 *
 * `fetch`, not HttpClient: this goes to a third party, and must not pick up our API's
 * interceptors and headers on the way. The browser's Referer identifies the app, which is
 * what the policy asks of a web client (a User-Agent cannot be set from a page).
 */
export async function geocodeAddress(
  address: string,
  settings: DeliverySettings,
  language: string
): Promise<LatLng | null> {
  const query = address.trim();
  if (!query) return null;

  await nominatimTurn();

  const params = new URLSearchParams({ format: 'jsonv2', limit: '1', q: query });
  if (settings.venue) {
    const radiusKm = Math.max(5, ...settings.areas.map(a => a.radiusKm));
    const dLat = radiusKm / 111;
    const dLng = radiusKm / (111 * Math.max(0.1, Math.cos(settings.venue.lat * Math.PI / 180)));
    params.set('viewbox', [
      settings.venue.lng - dLng, settings.venue.lat + dLat,
      settings.venue.lng + dLng, settings.venue.lat - dLat,
    ].join(','));
    params.set('bounded', '0');
  }
  if (language) {
    params.set('accept-language', language);
  }

  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const results = await response.json();
    const first = Array.isArray(results) ? results[0] : null;
    const lat = parseFloat(first?.lat);
    const lng = parseFloat(first?.lon);
    return isFinite(lat) && isFinite(lng) ? { lat, lng } : null;
  } catch {
    return null;
  }
}

/**
 * The address at a pin the guest moved, compact — "Vitosha 12, Sofia" — or null when
 * Nominatim has nothing there (or cannot be reached); the typed text is then kept.
 *
 * Called once the pin has settled, never while it is being dragged. Same rate limit as the
 * forward lookup.
 */
export async function reverseGeocode(pin: LatLng, language: string): Promise<string | null> {
  await nominatimTurn();

  const params = new URLSearchParams({
    format: 'jsonv2',
    lat: pin.lat.toFixed(7),
    lon: pin.lng.toFixed(7),
  });
  if (language) {
    params.set('accept-language', language);
  }

  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return compactAddress(await response.json());
  } catch {
    return null;
  }
}

/**
 * Street and number, then the town, from Nominatim's `address` parts. Its `display_name`
 * runs to district, municipality, province, postcode and country — far too long for the
 * courier's line, and the guest would have to delete most of it.
 */
export function compactAddress(result: any): string | null {
  const a = result?.address;
  if (!a || typeof a !== 'object') return null;

  const street = a.road || a.pedestrian || a.footway || a.path || a.square || a.place
    || a.neighbourhood || a.suburb || '';
  const line = [street, a.house_number].filter(Boolean).join(' ');
  const town = a.city || a.town || a.village || a.municipality || '';
  const compact = [line, town].filter(Boolean).join(', ');
  return compact || null;
}

